import type { LocationObject } from "expo-location/build/Location.types";

import { type KeyValue } from "@/schemas/api/tracking";
import {
  type DiagnosticItem,
  type DiagnosticRun,
} from "@/schemas/app/diagnostic";

export function convertDiagnosticRunToKeyValue(
  diagnosticRun: DiagnosticRun,
): KeyValue[] {
  const keyValues: KeyValue[] = [];

  for (const [category, items] of Object.entries(diagnosticRun)) {
    items.forEach((item: DiagnosticItem) => {
      keyValues.push({
        key: `${category}.${item.id}`, // Prefixing with category for clarity
        value: item.value,
      });
    });
  }

  return keyValues;
}

/**
 * Converts Expo location data to KeyValue array with properly formatted coordinates
 * @param location LocationObject from expo-location
 * @returns Array of KeyValue pairs representing the location data
 */
export function convertExpoLocationToKeyValue(
  location: LocationObject,
): KeyValue[] {
  const keyValues: KeyValue[] = [];

  // Handle the coords object
  if (location.coords) {
    const { coords } = location;

    // Process latitude and longitude with 6 decimal places precision
    if (coords.latitude !== undefined) {
      keyValues.push({
        key: "LOCATION.latitude",
        value: coords.latitude.toFixed(6),
      });
    }

    if (coords.longitude !== undefined) {
      keyValues.push({
        key: "LOCATION.longitude",
        value: coords.longitude.toFixed(6),
      });
    }

    // Add altitude with 2 decimal places if available
    if (coords.altitude !== undefined && coords.altitude !== null) {
      keyValues.push({
        key: "LOCATION.altitude",
        value: coords.altitude.toFixed(2),
      });
    }

    // Add accuracy metrics as whole numbers
    if (coords.accuracy !== undefined && coords.accuracy !== null) {
      keyValues.push({
        key: "LOCATION.accuracy",
        value: Math.round(coords.accuracy).toString(),
      });
    }

    if (
      coords.altitudeAccuracy !== undefined &&
      coords.altitudeAccuracy !== null
    ) {
      keyValues.push({
        key: "LOCATION.altitudeAccuracy",
        value: Math.round(coords.altitudeAccuracy).toString(),
      });
    }

    // Add heading as whole number if available
    if (coords.heading !== undefined && coords.heading !== null) {
      keyValues.push({
        key: "LOCATION.heading",
        value: Math.round(coords.heading).toString(),
      });
    }

    // Add speed if available
    if (coords.speed !== undefined && coords.speed !== null) {
      keyValues.push({
        key: "LOCATION.speed",
        value: coords.speed.toFixed(1), // One decimal place for speed
      });
    }
  }

  // Include timestamp if available
  if (location.timestamp) {
    keyValues.push({
      key: "LOCATION.timestamp",
      value: location.timestamp.toString(),
    });
  }

  // Include mocked status (whether the location is from a mock provider)
  if (location.mocked !== undefined) {
    keyValues.push({
      key: "LOCATION.mocked",
      value: location.mocked.toString(),
    });
  }

  return keyValues;
}
